import React, { useEffect, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import { ONE_AUTH_MODAL } from '@chegg-wt/core/state/modals/constants'
import {
  AuthModalData,
  AuthModalOrigin,
} from '@chegg-wt/core/components/Modals/AuthModal/types'
import { Config } from '@chegg-wt/core/config'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import { loggedInSelector, userRoleSelector } from '@chegg-wt/core/state/user/selectors'
import { AppState } from '@chegg-wt/core/state'
import { AppRoute, CitationValidityVariations, Project } from '@chegg-wt/core/types/core'
import { citationValidityVariationSelector } from '@chegg-wt/core/state/bibliography/selectors'
import { UserRole, UserRoles } from '@chegg-wt/core/constants/user'
import {
  currentProjectIdSelector,
  currentProjectSelector,
} from '@chegg-wt/core/state/currentProject/selectors'
import { navigated } from '@chegg-wt/core/state/app/actions'
import {
  emitComponentViewEvent,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import {
  Metadata,
  componentOrigins,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { CitationMetadata } from '@chegg-wt/core/types/autocite'
import * as Styled from './styled'
import { getBannerHeadings } from './utils'

interface ContainerProps {
  createAccount: (data: AuthModalData) => void
  projectData: Project
  citationValidityVariation: CitationValidityVariations
  isSourceSelectionPage?: boolean
  userRole: UserRole
  navigated: (route: AppRoute) => void
  citationValidityBannerRioEvent: (metadata: Metadata) => void
  createAccountBannerRioEvent: () => void
  citationValidityClick: (metadata: CitationMetadata) => void
  saveAllCitationClick: () => void
}

export const CREATE_ACCOUNT_BTN = 'create-account-btn'

export const CreateAccountBanner: React.FunctionComponent<ContainerProps> = ({
  createAccount,
  projectData,
  citationValidityVariation,
  isSourceSelectionPage = false,
  userRole,
  citationValidityBannerRioEvent,
  createAccountBannerRioEvent,
  citationValidityClick,
  saveAllCitationClick,
}) => {
  const projectCreateDate = projectData?.createdAt ?? null
  const [isRioSent, setIsRioSent] = useState<boolean>(false)
  const projectId = useSelector(currentProjectIdSelector)
  const metadata = { bibliographyId: projectId }

  const isLoggedIn = useSelector(loggedInSelector)

  const showCitationValidityBanner =
    citationValidityVariation !== CitationValidityVariations.CONTROL && projectCreateDate

  let showBanner = userRole === UserRoles.NOT_LOGGED_IN
  if (
    citationValidityVariation !== CitationValidityVariations.CONTROL &&
    !isSourceSelectionPage
  ) {
    showBanner = !!projectCreateDate
  }

  useEffect(() => {
    if (!isRioSent) {
      if (showBanner) {
        if (showCitationValidityBanner) {
          citationValidityBannerRioEvent(metadata)
        } else {
          createAccountBannerRioEvent()
        }
        setIsRioSent(true)
      }
    }
  }, [showCitationValidityBanner, isRioSent])

  const bannerHeading = showCitationValidityBanner
    ? getBannerHeadings(projectCreateDate)?.bannerHeading
    : `Keep all of your citations in one safe place`

  const bannerSubHeading = showCitationValidityBanner
    ? getBannerHeadings(projectCreateDate).bannerSubHeading
    : `Create an account to save all of your citations`
  const oneAuthData: AuthModalData = {
    defaultFlow: 'signup',
    origin: AuthModalOrigin.BIBLIOGRAPHY_PAGE,
  }
  return showBanner && !isLoggedIn && Config.showSourcePageAccountCreationBanner ? (
    <Styled.Box>
      <Styled.Container>
        <Styled.KeepCitations>{bannerHeading}</Styled.KeepCitations>
        <Styled.CreateAccount>{bannerSubHeading}</Styled.CreateAccount>
      </Styled.Container>
      <Styled.CreateButton
        data-test={CREATE_ACCOUNT_BTN}
        onClick={() => {
          if (showCitationValidityBanner) {
            citationValidityClick(metadata)
          } else {
            saveAllCitationClick()
          }
          createAccount(oneAuthData)
        }}
      >
        Create Account
      </Styled.CreateButton>
    </Styled.Box>
  ) : null
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  navigated: (route: AppRoute) => dispatch(navigated(route)),
  createAccount: (data: AuthModalData) =>
    dispatch(openModal({ name: ONE_AUTH_MODAL, data })),
  citationValidityBannerRioEvent: (metadata: Metadata) =>
    dispatch(
      emitComponentViewEvent({
        origin: componentOrigins.citationValidityBanner,
        metadata,
      })
    ),
  createAccountBannerRioEvent: () =>
    dispatch(
      emitComponentViewEvent({
        origin: componentOrigins.createAccountBanner,
      })
    ),
  citationValidityClick: (metadata: CitationMetadata) =>
    dispatch(
      userClick({
        origin: origins.citationValidityCreateAccountClick,
        citationMetadata: metadata,
      })
    ),
  saveAllCitationClick: () =>
    dispatch(userClick({ origin: origins.saveAllCitationsCreateAccountClick })),
})

const mapStateToProps = (state: AppState) => ({
  projectData: currentProjectSelector(state),
  citationValidityVariation: citationValidityVariationSelector(state),
  userRole: userRoleSelector(state),
})

export default connect(mapStateToProps, mapDispatchToProps)(CreateAccountBanner)
